import {
  GeneralPermissions,
  SettingsPermissions,
} from "@redotech/account-common/permissions";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { SettingNavigationProvider } from "@redotech/redo-merchant-app-common/global-search/setting-search/setting-navigator-context";
import { PermissionedRouteObject } from "@redotech/redo-merchant-app-common/routing/protected-routes";
import { SettingsSectionEnum } from "@redotech/redo-web/arbiter-components/navigation/redo-settings-nav";
import { breadcrumbSlot } from "@redotech/redo-web/breadcrumb";
import { Navigate, useLocation, useParams } from "react-router-dom";
import {
  catalogRoutePermission,
  INTEGRATIONS_HUB_PERMISSIONS,
} from "../../../integrations/catalog/catalog-data";
import {
  aiSettingsRoutes,
  inAiKnowledgeView,
} from "../../../setting/ai-agent/route";
import { conversionRoutes } from "../../../setting/conversion/route";
import { csatDeepLinkTarget } from "../../../setting/customer-satisfaction/csat-deep-links";
import { inFaqBuilder, inFaqHomeView } from "./faq-routes";
import { IntegrationsHubRedirect } from "./integrations-hub-redirect";
import { LegacyIntegrationVendorRedirect } from "./legacy-integration-vendor-redirect";

const attributeDefinitionsBreadcrumb = breadcrumbSlot();

const FaqRedirect = () => {
  return <Navigate replace to="/support-v2/faq/home" />;
};

/** CSAT moved to the support product; keep old deep links landing on the right sub-page. */
const CsatSettingsRedirect = () => {
  const { search } = useLocation();
  return <Navigate replace to={csatDeepLinkTarget(search)} />;
};

const WarehouseSettingsRedirect = () => {
  const { pathname, search } = useLocation();
  return (
    <Navigate
      replace
      to={`${pathname.replace(
        "/settings/order-management/returns-centers",
        "/settings/warehouse",
      )}${search}`}
    />
  );
};

const ChargebacksGeneralRedirect = () => {
  const { search } = useLocation();
  return <Navigate replace to={`../reclaim${search}`} />;
};

const UnifiedIntegrationsVendorRedirect = () => {
  const { slug } = useParams();
  const { search } = useLocation();
  return <Navigate replace to={`../../integrations/vendor/${slug}${search}`} />;
};

const InternationalIndexRedirect = () => {
  const canManageOutboundCustoms = useHasPermission(
    SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
  );
  const canManageReturnCustoms = useHasPermission(
    SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    SettingsPermissions.SET_SHIPPING_SETTINGS,
  );

  if (!canManageOutboundCustoms && canManageReturnCustoms) {
    return <Navigate replace to="returns" />;
  }

  return <Navigate replace to="customs" />;
};

const fullscreen = (url: string) => {
  return url.includes("ad-blocks/") && !url.endsWith("ad-blocks/");
};

const inNewFlowEditor = (url: string) => {
  return (
    url.includes("/flows/returns") ||
    url.includes("/flows/claims") ||
    url.includes("/flows/warranties") ||
    url.includes("/flows/withdrawals") ||
    inCompensationFlowEditor(url) ||
    inRestockingFlowEditor(url)
  );
};

const inCompensationFlowEditor = (url: string) => {
  return url.includes("compensation-flow-editor");
};

const inRestockingFlowEditor = (url: string) => {
  return url.includes("restocking-flow-editor");
};

const inLocationEditor = (url: string) => {
  return url.includes("location-flow-editor");
};

const inLabelsEditor = (url: string) => {
  return url.includes("labels-flow-editor");
};

const inForge = (url: string) => {
  return (
    (url.includes("reviews-displays-widgets/") &&
      !url.endsWith("reviews-displays-widgets/")) ||
    (url.includes("upsell-page/") && !url.endsWith("upsell-page/")) ||
    (url.includes("oms/automations") && !url.endsWith("oms/automations")) ||
    inFaqBuilder(url) ||
    url.includes("oms/warehouse-builder") ||
    url.includes("conversion/general/checkout-widgets-builder") ||
    inWorkOrderFlowBuilder(url)
  );
};

const inPhoneSetup = (url: string) => {
  return url.includes("phone-setup");
};

const inBilling = (url: string) => {
  return url.includes("/settings/billing");
};

const inAutomationsList = (url: string) => {
  return url.endsWith("oms/automations");
};

const inWorkOrderFlowsList = (url: string) => {
  return url.endsWith("oms/work-order-flows");
};

const inWorkOrderFlowBuilder = (url: string) => {
  return url.includes("oms/work-order-flows/");
};

const inProcessingSettings = (url: string) => {
  return (
    url.endsWith("settings/automations/processing") ||
    url.endsWith("settings/automations/processing/")
  );
};

const inReturnProcessingFlowBuilder = (url: string) => {
  return (
    url.includes("settings/automations/") &&
    !inProcessingSettings(url) &&
    !inCompensationFlowEditor(url) &&
    !inRestockingFlowEditor(url)
  );
};

const inPortalBuilder = (url: string) => {
  return url.includes("settings/portal-builder");
};

const inCoverageBuilder = (url: string) => {
  return url.includes("coverage-builder");
};

const inCoverageSettings = (url: string) => {
  return url.includes("settings/coverage") && !inCoverageBuilder(url);
};

const inShippingSettings = (url: string) => {
  return (
    url.includes("settings/shipping") &&
    !inLocationEditor(url) &&
    !inLabelsEditor(url)
  );
};

const inPackingSlipBuilder = (url: string) => {
  return url.includes("settings/oms/printing/packing-slips/");
};

const isWarehouseLayout = (url: string) => {
  return url.endsWith("oms/warehouse-layouts");
};

const isFullwidthTable = (url: string) => {
  return (
    url.includes("settings/attributes") ||
    url.includes("settings/exchanges/guidance")
  );
};

const inRuleEditor = (url: string) => {
  return (
    url.includes("settings/rules/edit-v3/") ||
    url.includes("settings/rules/edit-default/")
  );
};

function hidePadding(url: string) {
  return (
    inAiKnowledgeView(url) ||
    inAutomationsList(url) ||
    inWorkOrderFlowsList(url) ||
    fullscreen(url) ||
    inForge(url) ||
    inReturnProcessingFlowBuilder(url) ||
    inPhoneSetup(url) ||
    inBilling(url) ||
    isWarehouseLayout(url) ||
    inFaqHomeView(url) ||
    isFullwidthTable(url) ||
    (url.includes("customer-portal") && !inNewFlowEditor(url)) ||
    inPortalBuilder(url) ||
    inCoverageBuilder(url) ||
    inCoverageSettings(url) ||
    inShippingSettings(url) ||
    inPackingSlipBuilder(url) ||
    inRuleEditor(url)
  );
}

const settingsRoutesBySection: Record<
  SettingsSectionEnum,
  PermissionedRouteObject[] | PermissionedRouteObject
> = {
  [SettingsSectionEnum.GENERAL]: [
    {
      handle: {
        breadcrumb: "Billing",
        permission: [
          SettingsPermissions.SET_GENERAL_SETTINGS,
          SettingsPermissions.VIEW_BILLING,
          SettingsPermissions.SET_BILLING_SETTINGS,
        ],
      },
      path: "billing",
      children: [
        {
          index: true,
          lazy: async () => {
            const { BillingPage } =
              await import("../../../setting/billing/billing");
            return { Component: BillingPage };
          },
        },
        {
          handle: {
            breadcrumb: "Supplies",
            permission: SettingsPermissions.SET_BILLING_SETTINGS,
          },
          path: "supplies",
          lazy: async () => {
            const { RedoSuppliesBillingPage } =
              await import("../../../setting/billing/payment-methods/supplies-billing-page");
            return { Component: RedoSuppliesBillingPage };
          },
        },
        {
          handle: {
            breadcrumb: "Outbound",
            permission: SettingsPermissions.SET_GENERAL_SETTINGS,
          },
          path: "outbound",
          children: [
            {
              lazy: async () => {
                const { OutboundBillingDetail } =
                  await import("../../../setting/billing/products/outbound-billing-detail");
                return { Component: OutboundBillingDetail };
              },
              children: [
                { index: true, element: <Navigate replace to="general" /> },
                {
                  path: "general",
                  lazy: async () => {
                    const { BalanceTransactionTable } =
                      await import("../../../setting/billing/outbound-labels-ledger/balance-transaction-table");
                    return { Component: BalanceTransactionTable };
                  },
                },
                {
                  path: "balance",
                  lazy: async () => {
                    const { OutboundLabelBalancePage } =
                      await import("../../../setting/outbound-label-balance");
                    return { Component: OutboundLabelBalancePage };
                  },
                },
                {
                  path: "disputes",
                  lazy: async () => {
                    const { SurchargeDisputeTable } =
                      await import("../../../setting/billing/surcharge-disputes/surcharge-dispute-table");
                    return { Component: SurchargeDisputeTable };
                  },
                },
              ],
            },
            {
              async lazy() {
                const { InvoiceDetailPageWrapper, breadcrumb } =
                  await import("../../../setting/billing/outbound-labels-ledger/invoice-detail-page-wrapper");
                return {
                  Component: InvoiceDetailPageWrapper,
                  handle: {
                    breadcrumb,
                    permission: SettingsPermissions.SET_GENERAL_SETTINGS,
                  },
                };
              },
              path: "outbound-invoice/:invoiceId",
            },
          ],
        },
        {
          handle: {
            breadcrumb: "Returns",
            permission: SettingsPermissions.SET_GENERAL_SETTINGS,
          },
          path: "returns",
          children: [
            {
              lazy: async () => {
                const { ReturnsBillingDetail } =
                  await import("../../../setting/billing/products/returns/returns-billing-detail");
                return { Component: ReturnsBillingDetail };
              },
              children: [
                {
                  index: true,
                  lazy: async () => {
                    const { ReturnsBillingIndexRedirect } =
                      await import("../../../setting/billing/products/returns/returns-billing-detail");
                    return { Component: ReturnsBillingIndexRedirect };
                  },
                },
                {
                  path: "general",
                  lazy: async () => {
                    const { ReturnsBilling } =
                      await import("../../../setting/billing/products/returns/returns-billing");
                    return { Component: ReturnsBilling };
                  },
                },
              ],
            },
          ],
        },
        {
          handle: {
            breadcrumb: "Inventory management",
            permission: SettingsPermissions.SET_GENERAL_SETTINGS,
          },
          path: "ims",
          children: [
            {
              lazy: async () => {
                const { ImsBillingDetail } =
                  await import("../../../setting/billing/products/ims/ims-billing-detail");
                return { Component: ImsBillingDetail };
              },
              children: [
                {
                  index: true,
                  element: <Navigate replace to="subscription" />,
                },
                {
                  path: "subscription",
                  lazy: async () => {
                    const { ImsBilling } =
                      await import("../../../setting/billing/products/ims/ims-billing");
                    return { Component: ImsBilling };
                  },
                },
                {
                  path: "forecasting",
                  lazy: async () => {
                    const { ImsForecastingBilling } =
                      await import("../../../setting/billing/products/ims/ims-forecasting-billing");
                    return { Component: ImsForecastingBilling };
                  },
                },
              ],
            },
          ],
        },
        {
          path: ":productSlug",
          lazy: async () => {
            const { BillingProductDetail, productBreadcrumb } =
              await import("../../../setting/billing/billing-product-detail");
            return {
              Component: BillingProductDetail,
              handle: {
                breadcrumb: productBreadcrumb,
                permission: SettingsPermissions.SET_GENERAL_SETTINGS,
              },
            };
          },
        },
      ],
    },
    {
      handle: {
        breadcrumb: "Brand kit",
        permission: SettingsPermissions.SET_GENERAL_SETTINGS,
      },
      path: "brand-kit",
      lazy: async () => {
        const { BrandKitPage } =
          await import("@redotech/redo-merchant-general-app/setting/brand-kit/brand-kit");
        return { Component: BrandKitPage };
      },
    },
    {
      handle: {
        breadcrumb: "Communication",
        permission: SettingsPermissions.SET_GENERAL_SETTINGS,
      },
      path: "communication",
      lazy: async () => {
        const { CommunicationPage } =
          await import("@redotech/redo-merchant-general-app/setting/communication/communication");
        return { Component: CommunicationPage };
      },
    },
    {
      handle: {
        breadcrumb: "Customer Portal",
        permission: [
          SettingsPermissions.SET_GENERAL_SETTINGS,
          SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
        ],
      },
      path: "customer-portal",
      children: [
        {
          index: true,
          lazy: async () => {
            const { CustomerPortalPage: CustomerPortal } =
              await import("@redotech/redo-merchant-general-app/setting/customer-portal/customer-portal-page");
            return { Component: CustomerPortal };
          },
        },
        {
          handle: {
            breadcrumb: "Flows",
            breadcrumbNavigate: false,
            permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
          },
          path: "flows",
          children: [
            {
              handle: {
                breadcrumb: "Returns",
                permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
              },
              path: "returns",
              lazy: async () => {
                const { ReturnsFlowEditor } =
                  await import("../../../setting/flows/returns-flow-editor");
                return { element: <ReturnsFlowEditor /> };
              },
            },
            {
              handle: {
                breadcrumb: "AB test analytics",
                permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
              },
              path: "ab-test-analytics",
              lazy: async () => {
                const { ReturnFlowABTestAnalytics } =
                  await import("../../../setting/return-flow/ab-test-analytics");
                return { element: <ReturnFlowABTestAnalytics /> };
              },
            },
            {
              handle: {
                breadcrumb: "Returns-finalize",
                permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
              },
              path: "returns-finalize",
              lazy: async () => {
                const { ReturnsFinalizeFlowEditor } =
                  await import("../../../setting/flows/returns-finalize-flow-editor");
                return { element: <ReturnsFinalizeFlowEditor /> };
              },
            },
            {
              handle: {
                breadcrumb: "Claims",
                permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
              },
              path: "claims",
              lazy: async () => {
                const { ClaimsFlowEditor } =
                  await import("../../../setting/flows/claims-flow-editor");
                return { element: <ClaimsFlowEditor /> };
              },
            },
            {
              handle: {
                breadcrumb: "Claims-finalize",
                permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
              },
              path: "claims-finalize",
              lazy: async () => {
                const { ClaimsFinalizeFlowEditor } =
                  await import("../../../setting/flows/claims-finalize-flow-editor");
                return { element: <ClaimsFinalizeFlowEditor /> };
              },
            },
            {
              handle: {
                breadcrumb: "Warranties",
                permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
              },
              path: "warranties",
              lazy: async () => {
                const { WarrantiesFlowEditor } =
                  await import("../../../setting/flows/warranties-flow-editor");
                return { element: <WarrantiesFlowEditor /> };
              },
            },
            {
              handle: {
                breadcrumb: "Warranties-finalize",
                permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
              },
              path: "warranties-finalize",
              lazy: async () => {
                const { WarrantiesFinalizeFlowEditor } =
                  await import("../../../setting/flows/warranties-finalize-flow-editor");
                return { element: <WarrantiesFinalizeFlowEditor /> };
              },
            },
            {
              handle: {
                breadcrumb: "Warranties-registration",
                permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
              },
              path: "warranties-registration",
              lazy: async () => {
                const { WarrantiesRegistrationFlowEditor } =
                  await import("../../../setting/flows/warranties-registration-flow-editor");
                return { element: <WarrantiesRegistrationFlowEditor /> };
              },
            },
            {
              handle: {
                breadcrumb: "Withdrawals",
                permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
              },
              path: "withdrawals",
              lazy: async () => {
                const { WithdrawalsFlowEditor } =
                  await import("../../../setting/flows/withdrawals-flow-editor");
                return { element: <WithdrawalsFlowEditor /> };
              },
            },
          ],
        },
        {
          handle: {
            breadcrumb: "Exchange Agent Guidance",
            permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
          },
          path: "guidance",
          lazy: async () => {
            const { ExchangeAgentGuidancePage } =
              await import("../../../setting/exchange-agent-guidance/exchange-agent-guidance-page");
            return { Component: ExchangeAgentGuidancePage };
          },
        },
        {
          handle: {
            breadcrumb: "Bundles",
            permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
          },
          path: "bundles",
          lazy: async () => {
            const { BundlesPage } =
              await import("../../../setting/bundles/bundles");
            return { Component: BundlesPage };
          },
        },
      ],
    },
    // We moved /return-portal -> /customer-portal, so we'll redirect users just in case.
    {
      path: "return-portal",
      element: <Navigate replace to="../customer-portal" />,
    },
    {
      handle: {
        breadcrumb: "Portal Builder",
        permission: [
          SettingsPermissions.SET_GENERAL_SETTINGS,
          SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
        ],
      },
      path: "portal-builder",
      lazy: async () => {
        const { CustomerPortalBuilderSettingsPage } =
          await import("@redotech/redo-merchant-general-app/setting/customer-portal-builder/customer-portal-builder-settings");
        return { Component: CustomerPortalBuilderSettingsPage };
      },
    },
    {
      handle: {
        breadcrumb: "Customer widget",
        permission: [
          SettingsPermissions.SET_GENERAL_SETTINGS,
          SettingsPermissions.SET_AI_SETTINGS,
          SettingsPermissions.SET_CUSTOMER_ACCOUNTS_SETTINGS,
          SettingsPermissions.SET_SUPPORT_SETTINGS,
        ],
      },
      path: "customer-widget",
      lazy: async () => {
        const { CustomerWidgetSettingsPage } =
          await import("../../../setting/customer-widget/customer-widget-settings");
        return { Component: CustomerWidgetSettingsPage };
      },
    },
    {
      handle: {
        breadcrumb: "Developer",
        permission: GeneralPermissions.MANAGE_TEAM_DEVELOPMENT,
      },
      path: "developer",
      children: [
        {
          path: "",
          lazy: async () => {
            const { DeveloperPage } =
              await import("@redotech/redo-merchant-general-app/setting/developer");
            return { Component: DeveloperPage };
          },
        },
        {
          handle: { breadcrumb: "Webhook Details" },
          path: "webhooks/:webhookId",
          lazy: async () => {
            const { WebhookDetailPage } =
              await import("@redotech/redo-merchant-general-app/setting/developer/webhook-detail-page");
            return { Component: WebhookDetailPage };
          },
        },
      ],
    },
    { path: "invoices", element: <Navigate replace to="../billing/bills" /> },
    {
      handle: {
        breadcrumb: "Notifications",
        permission: SettingsPermissions.SET_GENERAL_SETTINGS,
      },
      path: "notifications",
      lazy: async () => {
        const { NotificationsPage } =
          await import("@redotech/redo-merchant-general-app/setting/notification/notifications");
        return { Component: NotificationsPage };
      },
    },
    {
      handle: {
        breadcrumb: "Users",
        permission: GeneralPermissions.MANAGE_TEAM_USERS,
      },
      path: "users",
      children: [
        {
          path: "",
          lazy: async () => {
            const { UsersPage } =
              await import("@redotech/redo-merchant-general-app/setting/user/user");
            return { Component: UsersPage };
          },
        },
        {
          handle: { breadcrumb: "User information" },
          path: ":userId",
          lazy: async () => {
            const { ProfilePage } =
              await import("@redotech/redo-merchant-general-app/setting/user/profile-page");
            return { Component: ProfilePage };
          },
        },
      ],
    },
    {
      handle: {
        breadcrumb: "Roles",
        permission: GeneralPermissions.MANAGE_TEAM_ROLES,
      },
      path: "roles",
      lazy: async () => {
        const { RolesSettingsPage } =
          await import("@redotech/redo-merchant-general-app/setting/roles/roles-settings");
        return { Component: RolesSettingsPage };
      },
    },
    {
      handle: {
        breadcrumb: "Groups",
        permission: GeneralPermissions.MANAGE_TEAM_USERS,
      },
      path: "user-groups",
      lazy: async () => {
        const { UserGroupsPage } =
          await import("../../../setting/user-groups/user-groups-page");
        return { Component: UserGroupsPage };
      },
    },
  ],

  [SettingsSectionEnum.PERSONAL]: [
    {
      handle: { breadcrumb: "Personal" },
      path: "personal",
      children: [
        { index: true, element: <Navigate replace to="profile" /> },
        {
          handle: { breadcrumb: "Profile settings" },
          path: "profile",
          lazy: async () => {
            const { ProfilePage } =
              await import("@redotech/redo-merchant-general-app/setting/user/profile-page");
            return { Component: ProfilePage };
          },
        },
      ],
    },
  ],
  [SettingsSectionEnum.AI]: aiSettingsRoutes,
  [SettingsSectionEnum.CONVERSION]: conversionRoutes,
  [SettingsSectionEnum.CHARGEBACKS]: {
    path: "chargebacks",
    handle: { breadcrumb: "Chargebacks" },
    children: [
      { index: true, element: <Navigate replace to="reclaim" /> },
      { path: "general", element: <ChargebacksGeneralRedirect /> },
      {
        path: "profile",
        handle: { breadcrumb: "Reclaim Profile" },
        lazy: async () => {
          const { ChargebacksProfileSettingsPage } =
            await import("../../../setting/chargebacks/chargebacks-profile-settings-page");
          return { Component: ChargebacksProfileSettingsPage };
        },
      },
      {
        path: "reclaim",
        handle: { breadcrumb: "Reclaim" },
        lazy: async () => {
          const { ChargebacksReclaimSettingsPage } =
            await import("../../../setting/chargebacks/chargebacks-reclaim-settings-page");
          return { Component: ChargebacksReclaimSettingsPage };
        },
      },
      {
        path: "alerts",
        handle: { breadcrumb: "Alerts" },
        lazy: async () => {
          const { ChargebacksAlertsSettingsPage } =
            await import("../../../setting/chargebacks/chargebacks-alerts-settings-page");
          return { Component: ChargebacksAlertsSettingsPage };
        },
      },
      {
        path: "resolve",
        handle: { breadcrumb: "Resolve" },
        lazy: async () => {
          const { ChargebacksResolveSettingsPage } =
            await import("../../../setting/chargebacks/chargebacks-resolve-settings-page");
          return { Component: ChargebacksResolveSettingsPage };
        },
      },
      {
        path: "integrations",
        handle: { breadcrumb: "Integrations" },
        element: (
          <IntegrationsHubRedirect products="reclaim" to="../../integrations" />
        ),
      },
    ],
  },
  [SettingsSectionEnum.CUSTOMER_ACCOUNTS]: [
    {
      handle: {
        breadcrumb: "Ad blocks",
        permission: SettingsPermissions.SET_CUSTOMER_ACCOUNTS_SETTINGS,
      },
      path: "customer-accounts-ad-blocks",
      children: [
        {
          index: true,
          lazy: async () => {
            const { AdBlocksPage } =
              await import("../../../setting/customer-accounts/ad-blocks/ad-blocks-drawer");
            return { Component: AdBlocksPage };
          },
        },
        {
          path: ":adBlockId",
          handle: {
            breadcrumb: "Edit Ad Block",
            permission: SettingsPermissions.SET_CUSTOMER_ACCOUNTS_SETTINGS,
          },
          lazy: async () => {
            const { AdBlockBuilder } =
              await import("../../../setting/customer-accounts/ad-blocks/ad-blocks-builder");
            return { element: <AdBlockBuilder /> };
          },
        },
      ],
    },
    {
      handle: {
        breadcrumb: "Integrations",
        permission: SettingsPermissions.SET_CUSTOMER_ACCOUNTS_SETTINGS,
      },
      path: "customer-accounts-integrations",
      element: <IntegrationsHubRedirect to="../integrations" />,
    },
    {
      handle: {
        breadcrumb: "Storefront display",
        permission: SettingsPermissions.SET_CUSTOMER_ACCOUNTS_SETTINGS,
      },
      path: "customer-accounts-storefront-display",
      lazy: async () => {
        const { StorefrontDisplaysPage } =
          await import("../../../setting/customer-accounts/storefront-displays");
        return { Component: StorefrontDisplaysPage };
      },
    },
  ],

  [SettingsSectionEnum.MARKETING]: [
    {
      handle: {
        breadcrumb: "Audience",
        permission: SettingsPermissions.SET_MARKETING_SETTINGS,
      },
      path: "audience",
      lazy: async () => {
        const { AudienceSettingsPage } =
          await import("../../../setting/marketing/audience-settings");
        return { Component: AudienceSettingsPage };
      },
    },
    {
      handle: {
        breadcrumb: "Email Domain",
        permission: SettingsPermissions.SET_MARKETING_SETTINGS,
      },
      path: "email-domain",
      lazy: async () => {
        const { EmailDomainPage } =
          await import("../../../setting/marketing/email-domain/email-domain");
        return { Component: EmailDomainPage };
      },
    },
    {
      handle: {
        breadcrumb: "SMS Marketing",
        permission: SettingsPermissions.SET_MARKETING_SETTINGS,
      },
      path: "sms-marketing",
      lazy: async () => {
        const { SmsSettingsPage } =
          await import("../../../setting/marketing/sms-settings");
        return { Component: SmsSettingsPage };
      },
    },
    {
      handle: {
        breadcrumb: "Keywords",
        permission: SettingsPermissions.SET_MARKETING_SETTINGS,
      },
      path: "subscribe-keywords",
      lazy: async () => {
        const { SubscribeKeywordsTable } =
          await import("../../../setting/marketing/subscribe-keywords/subscribe-keywords");
        return { Component: SubscribeKeywordsTable };
      },
    },
    {
      handle: {
        breadcrumb: "Integrations",
        permission: SettingsPermissions.SET_MARKETING_SETTINGS,
      },
      path: "marketing-integrations",
      children: [
        {
          index: true,
          element: <IntegrationsHubRedirect to="../integrations" />,
        },
        {
          handle: {
            breadcrumb: "Orders API Documentation",
            permission: SettingsPermissions.SET_MARKETING_SETTINGS,
          },
          path: "orders-api-documentation",
          lazy: async () => {
            const { OrdersApiDocumentation } =
              await import("../../../setting/marketing/integrations/orders-api-documentation");
            return { Component: OrdersApiDocumentation };
          },
        },
      ],
    },
    {
      handle: {
        breadcrumb: "Offer page configuration",
        permission: SettingsPermissions.SET_MARKETING_SETTINGS,
      },
      path: "offer-page-configuration",
      lazy: async () => {
        const { OfferPageConfigurationPage } =
          await import("../../../setting/marketing/offer-page-configuration");
        return { Component: OfferPageConfigurationPage };
      },
    },
    {
      handle: {
        breadcrumb: "Recover",
        permission: SettingsPermissions.SET_MARKETING_SETTINGS,
      },
      path: "recover-settings",
      lazy: async () => {
        const { RecoverSettingsPage } =
          await import("../../../setting/recover/recover-settings");
        return { Component: RecoverSettingsPage };
      },
    },
    {
      handle: {
        breadcrumb: "Attribution",
        permission: SettingsPermissions.SET_MARKETING_SETTINGS,
      },
      path: "attribution",
      lazy: async () => {
        const { AttributionSettingsPage } =
          await import("../../../setting/attribution/attribution-settings");
        return { Component: AttributionSettingsPage };
      },
    },
    {
      handle: {
        breadcrumb: "Marketing Agent",
        permission: SettingsPermissions.SET_MARKETING_SETTINGS,
      },
      path: "marketing-agent",
      lazy: async () => {
        const { MarketingAgentSettingsPage } =
          await import("@redotech/redo-merchant-marketing-app/marketing-agent/marketing-agent-settings-page");
        return { Component: MarketingAgentSettingsPage };
      },
    },
  ],

  [SettingsSectionEnum.ORDER_TRACKING]: [
    {
      handle: {
        breadcrumb: "General",
        permission: SettingsPermissions.SET_MARKETING_SETTINGS,
      },
      path: "order-tracking",
      lazy: async () => {
        const { OrderTrackingSettings } =
          await import("../../../setting/order-tracking/order-tracking-settings");
        return { Component: OrderTrackingSettings };
      },
    },
    {
      handle: {
        breadcrumb: "Integrations",
        permission: SettingsPermissions.SET_MARKETING_SETTINGS,
      },
      path: "order-tracking-integrations",
      element: <IntegrationsHubRedirect to="../integrations" />,
    },
  ],

  [SettingsSectionEnum.PRODUCTS]: [
    {
      handle: {
        breadcrumb: "Products",
        permission: SettingsPermissions.SET_GENERAL_SETTINGS,
      },
      path: "products",
      async lazy() {
        const { ProductPage } =
          await import("../../../setting/product/product");
        return { Component: ProductPage };
      },
    },
    {
      handle: {
        breadcrumb: "Attribute Definitions",
        permission: SettingsPermissions.SET_GENERAL_SETTINGS,
      },
      path: "attributes",
      children: [
        {
          index: true,
          lazy: async () => {
            const { AttributeDefinitionsLandingPage } =
              await import("../../../setting/attribute-definitions");
            return { Component: AttributeDefinitionsLandingPage };
          },
        },
        {
          handle: {
            breadcrumb: attributeDefinitionsBreadcrumb,
            permission: SettingsPermissions.SET_GENERAL_SETTINGS,
          },
          path: ":ownerType",
          async lazy() {
            const { AttributeDefinitionsPage } =
              await import("../../../setting/attribute-definitions");
            return {
              element: (
                <AttributeDefinitionsPage
                  breadcrumb={attributeDefinitionsBreadcrumb}
                />
              ),
            };
          },
        },
      ],
    },
  ],

  [SettingsSectionEnum.RETURNS_AND_CLAIMS]: [
    {
      handle: {
        breadcrumb: "Assessments",
        permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
      },
      path: "assessments",
      lazy: async () => {
        const { AssessmentsWithTabsPage } =
          await import("../../../setting/assessments/assessments-with-tabs");
        return { Component: AssessmentsWithTabsPage };
      },
    },
    {
      handle: {
        breadcrumb: "Automations",
        permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
      },
      path: "automations",
      children: [
        {
          path: "",
          lazy: async () => {
            const { AutomationsPage } =
              await import("../../../setting/automation");
            return { Component: AutomationsPage };
          },
        },
        {
          handle: {
            breadcrumb: "Processing",
            permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
          },
          path: "processing",
          children: [
            {
              index: true,
              lazy: async () => {
                const { ProcessingPage } =
                  await import("../../../setting/automation/processing/processing-page");
                return { Component: ProcessingPage };
              },
            },
            {
              handle: {
                breadcrumb: "Compensation flow",
                permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
              },
              path: "compensation-flow-editor",
              lazy: async () => {
                const { CompensationFlowEditor } =
                  await import("../../../setting/flow-editor/compensation-flow/compensation-flow-editor");
                return { Component: CompensationFlowEditor };
              },
            },
            {
              handle: {
                breadcrumb: "Restocking flow",
                permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
              },
              path: "restocking-flow-editor",
              lazy: async () => {
                const { RestockingFlowEditor } =
                  await import("../../../setting/flow-editor/restocking-flow/restocking-flow-editor");
                return { Component: RestockingFlowEditor };
              },
            },
            {
              handle: {
                breadcrumb: "Return processing flows",
                permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
              },
              path: ":flowId",
              lazy: async () => {
                const { ReturnProcessingFlowBuilder } =
                  await import("../../../setting/return-processing-flows/return-processing-flow-builder");
                return { Component: ReturnProcessingFlowBuilder };
              },
            },
          ],
        },
      ],
    },
    {
      handle: {
        breadcrumb: "Coverage",
        breadcrumbNavigate: false,
        permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
      },
      path: "coverage",
      children: [
        {
          index: true,
          lazy: async () => {
            const { CoveragePage: CoverageSettings } =
              await import("../../../setting/coverage/coverage");
            return { Component: CoverageSettings };
          },
        },
        {
          handle: { breadcrumb: "Coverage Builder" },
          path: "coverage-builder",
          lazy: async () => {
            const { CoverageBuilderPage } =
              await import("../../../setting/coverage/coverage-builder/coverage-builder");
            return { Component: CoverageBuilderPage };
          },
        },
        {
          handle: { breadcrumb: "Info Modal" },
          path: "info-modal",
          lazy: async () => {
            const { InfoModalPage } =
              await import("../../../setting/coverage/info-modal");
            return { Component: InfoModalPage };
          },
        },
        {
          handle: { breadcrumb: "Custom Logo" },
          path: "custom-logo",
          lazy: async () => {
            const { CustomLogoPage } =
              await import("../../../setting/coverage/custom-logo");
            return { Component: CustomLogoPage };
          },
        },
      ],
    },
    {
      handle: {
        breadcrumb: "Exchanges",
        permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
      },
      path: "exchanges",
      lazy: async () => {
        const { ExchangesPage: Exchanges } =
          await import("../../../setting/exchanges");
        return { Component: Exchanges };
      },
    },
    {
      path: "exchanges/guidance",
      element: <Navigate replace to="../customer-portal/guidance" />,
    },
    {
      handle: {
        breadcrumb: "Warehouse",
        permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
      },
      path: "warehouse",
      children: [
        {
          path: "",
          lazy: async () => {
            const { ReturnsCentersIndexPage } =
              await import("../../../setting/managed-returns/returns-centers-index");
            return { Component: ReturnsCentersIndexPage };
          },
        },
        {
          handle: {
            breadcrumb: "Processing flow",
            permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
          },
          path: "processing-flow",
          lazy: async () => {
            const { WarehouseProcessingFlowEditor } =
              await import("../../../setting/flows/warehouse-processing-flow-editor");
            return { element: <WarehouseProcessingFlowEditor /> };
          },
        },
        {
          handle: {
            breadcrumb: "Photo guidance",
            permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
          },
          path: "photo-guidance",
          lazy: async () => {
            const { PhotoInstructionsPage } =
              // redont-allow-dynamic-import: route-level code splitting
              await import("@redotech/redo-merchant-fulfillment-app/setting/photo-instructions/photo-instructions-page");
            return { Component: PhotoInstructionsPage };
          },
        },
        {
          handle: {
            breadcrumb: "Integration",
            permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
          },
          path: "integrations/:provider",
          children: [
            {
              path: "",
              lazy: async () => {
                const { IntegrationDetailPage } =
                  await import("../../../setting/managed-returns/integration-detail");
                return { Component: IntegrationDetailPage };
              },
            },
            {
              handle: {
                breadcrumb: "Product mappings",
                permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
              },
              path: "product-mappings",
              lazy: async () => {
                const { ProductMappingsPage } =
                  await import("../../../setting/managed-returns/product-mappings");
                return { Component: ProductMappingsPage };
              },
            },
          ],
        },
      ],
    },
    {
      handle: {
        breadcrumb: "Integrations",
        permission: [...INTEGRATIONS_HUB_PERMISSIONS],
      },
      path: "unified-integrations",
      children: [
        { index: true, element: <Navigate replace to="../integrations" /> },
        {
          path: "vendor/:slug",
          element: <UnifiedIntegrationsVendorRedirect />,
        },
      ],
    },
    {
      handle: {
        breadcrumb: "Integrations",
        permission: [...INTEGRATIONS_HUB_PERMISSIONS],
      },
      path: "integrations",
      children: [
        {
          path: "",
          lazy: async () => {
            const { UnifiedIntegrationsPage } =
              await import("../../../integrations/pages/unified-integrations-page");
            return { Component: UnifiedIntegrationsPage };
          },
        },
        {
          handle: {
            breadcrumb: "Integration",
            permission: [...INTEGRATIONS_HUB_PERMISSIONS],
          },
          path: "vendor/:slug",
          lazy: async () => {
            const { IntegrationVendorPage } =
              await import("../../../integrations/pages/integration-vendor-page");
            return { Component: IntegrationVendorPage };
          },
        },
        {
          path: "comment-sold",
          element: <LegacyIntegrationVendorRedirect slug="comment-sold" />,
        },
        {
          path: "netsuite",
          element: <LegacyIntegrationVendorRedirect slug="netsuite" />,
        },
        {
          path: "indigo8",
          element: <LegacyIntegrationVendorRedirect slug="indigo8" />,
        },
        {
          path: "odoo",
          element: <LegacyIntegrationVendorRedirect slug="odoo" />,
        },
        {
          path: "shipmonk",
          element: <LegacyIntegrationVendorRedirect slug="shipmonk" />,
        },
        {
          path: "masonhub",
          element: <LegacyIntegrationVendorRedirect slug="masonhub" />,
        },
        {
          path: "trackstar",
          element: <LegacyIntegrationVendorRedirect slug="trackstar" />,
        },
        {
          path: "aims360",
          element: <LegacyIntegrationVendorRedirect slug="aims360" />,
        },
        {
          path: "apparel-magic",
          element: <LegacyIntegrationVendorRedirect slug="apparel-magic" />,
        },
        {
          path: "authorizenet",
          element: <LegacyIntegrationVendorRedirect slug="authorizenet" />,
        },
        {
          path: "blue-cherry",
          element: <LegacyIntegrationVendorRedirect slug="blue-cherry" />,
        },
        {
          path: "controlport",
          element: <LegacyIntegrationVendorRedirect slug="controlport" />,
        },
        {
          path: "mintsoft",
          element: <LegacyIntegrationVendorRedirect slug="mintsoft" />,
        },
        {
          path: "blue-yonder",
          element: <LegacyIntegrationVendorRedirect slug="blue-yonder" />,
        },
        {
          path: "brightpearl",
          element: <LegacyIntegrationVendorRedirect slug="brightpearl" />,
        },
        {
          path: "cloudx",
          element: <LegacyIntegrationVendorRedirect slug="cloudx" />,
        },
        {
          path: "comment-sold/manage",
          element: <LegacyIntegrationVendorRedirect slug="comment-sold" />,
        },
        {
          path: "bubblehouse",
          element: <LegacyIntegrationVendorRedirect slug="bubblehouse" />,
        },
        {
          path: "creditsyard",
          element: <LegacyIntegrationVendorRedirect slug="creditsyard" />,
        },
        {
          path: "custom-return",
          element: <LegacyIntegrationVendorRedirect slug="custom-return" />,
        },
        {
          path: "aptos",
          element: <LegacyIntegrationVendorRedirect slug="aptos" />,
        },
        {
          path: "deposco",
          element: <LegacyIntegrationVendorRedirect slug="deposco" />,
        },
        {
          path: "extensiv",
          element: <LegacyIntegrationVendorRedirect slug="extensiv" />,
        },
        {
          path: "flexport",
          element: <LegacyIntegrationVendorRedirect slug="flexport" />,
        },
        {
          path: "globale",
          element: <LegacyIntegrationVendorRedirect slug="globale" />,
        },
        {
          path: "idf-shipping",
          element: <LegacyIntegrationVendorRedirect slug="idf-shipping" />,
        },
        {
          path: "ecwid",
          element: <LegacyIntegrationVendorRedirect slug="ecwid" />,
        },
        {
          path: "inmar",
          element: <LegacyIntegrationVendorRedirect slug="inmar" />,
        },
        {
          path: "katana",
          element: <LegacyIntegrationVendorRedirect slug="katana" />,
        },
        {
          handle: {
            breadcrumb: "Cordial",
            permission: catalogRoutePermission("cordial"),
          },
          path: "cordial",
          lazy: async () => {
            const { CordialIntegrationSettingsPage } =
              await import("../../../setting/cordial-integration-settings");
            return { Component: CordialIntegrationSettingsPage };
          },
        },
        {
          path: "klaviyo",
          element: <LegacyIntegrationVendorRedirect slug="klaviyo" />,
        },
        {
          path: "loop",
          element: <LegacyIntegrationVendorRedirect slug="loop" />,
        },
        {
          path: "narvar",
          element: <LegacyIntegrationVendorRedirect slug="narvar" />,
        },
        {
          path: "return-logic",
          element: <LegacyIntegrationVendorRedirect slug="return-logic" />,
        },
        {
          path: "luminous",
          element: <LegacyIntegrationVendorRedirect slug="luminous" />,
        },
        {
          path: "purple-dot",
          element: <LegacyIntegrationVendorRedirect slug="purple-dot" />,
        },
        {
          path: "refundid",
          element: <LegacyIntegrationVendorRedirect slug="refundid" />,
        },
        {
          path: "rise-ai",
          element: <LegacyIntegrationVendorRedirect slug="rise-ai" />,
        },
        {
          path: "loopz",
          element: <LegacyIntegrationVendorRedirect slug="loopz" />,
        },
        {
          path: "rivo",
          element: <LegacyIntegrationVendorRedirect slug="rivo" />,
        },
        {
          path: "seko",
          element: <LegacyIntegrationVendorRedirect slug="seko" />,
        },
        {
          path: "shipbob",
          element: <LegacyIntegrationVendorRedirect slug="shipbob" />,
        },
        {
          path: "shipedge",
          element: <LegacyIntegrationVendorRedirect slug="shipedge" />,
        },
        {
          path: "shiphero",
          element: <LegacyIntegrationVendorRedirect slug="shiphero" />,
        },
        {
          path: "ryder",
          element: <LegacyIntegrationVendorRedirect slug="ryder" />,
        },
        {
          path: "cin7-omni",
          element: <LegacyIntegrationVendorRedirect slug="cin7-omni" />,
        },
        {
          path: "teamwork-commerce",
          element: <LegacyIntegrationVendorRedirect slug="teamwork-commerce" />,
        },
        {
          path: "tiktok-shop",
          element: <LegacyIntegrationVendorRedirect slug="tiktok-shop" />,
        },
      ],
    },
    {
      handle: {
        breadcrumb: "Merchant-created returns",
        permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
      },
      path: "permissions",
      lazy: async () => {
        const { PermissionsPage } =
          await import("../../../setting/permissions/permissions");
        return { Component: PermissionsPage };
      },
    },
    {
      handle: {
        breadcrumb: "Point of sale",
        permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
      },
      path: "pos",
      children: [
        {
          path: "",
          lazy: async () => {
            const { PointOfSalePage } =
              await import("../../../setting/point-of-sale/point-of-sale-page");
            return { Component: PointOfSalePage };
          },
        },
        {
          handle: {
            breadcrumb: "Integration locations",
            permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
          },
          path: "integration-locations",
          lazy: async () => {
            const { IntegrationLocationsPage } =
              await import("../../../setting/point-of-sale/integration-locations-page");
            return { Component: IntegrationLocationsPage };
          },
        },
      ],
    },
    {
      handle: {
        breadcrumb: "Refunds & store credit",
        permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
      },
      path: "processing",
      lazy: async () => {
        const { ProcessingPage } = await import("../../../setting/processing");
        return { Component: ProcessingPage };
      },
    },
    {
      handle: {
        breadcrumb: "Processing groups",
        permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
      },
      path: "processing-groups",
      children: [
        {
          path: "",
          lazy: async () => {
            const { ProcessingGroupsPage } =
              await import("@redotech/redo-merchant-fulfillment-app/setting/processing-groups");
            return { Component: ProcessingGroupsPage };
          },
        },
        {
          handle: {
            breadcrumb: "Group details",
            permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
          },
          path: ":groupId",
          lazy: async () => {
            const { ProcessingGroupDetailPage } =
              await import("@redotech/redo-merchant-fulfillment-app/setting/processing-group-detail");
            return { Component: ProcessingGroupDetailPage };
          },
        },
      ],
    },
    {
      handle: {
        breadcrumb: "Post processing",
        permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
      },
      path: "post-processing",
      lazy: async () => {
        const { PostProcessingPage } =
          await import("@redotech/redo-merchant-fulfillment-app/setting/post-processing");
        return { Component: PostProcessingPage };
      },
    },
    {
      handle: {
        breadcrumb: "Consolidation",
        permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
      },
      path: "consolidation",
      lazy: async () => {
        const { ConsolidationSettingsPage } =
          // redont-allow-dynamic-import: route-level code splitting
          await import("@redotech/redo-merchant-fulfillment-app/setting/consolidation");
        return { Component: ConsolidationSettingsPage };
      },
    },
    {
      path: "sort-destinations",
      element: <Navigate replace to="../post-processing" />,
    },
    {
      handle: {
        breadcrumb: "Return detail view",
        permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
      },
      path: "returns-ui",
      lazy: async () => {
        const { ReturnsUiPage } =
          await import("../../../setting/returns-ui/returns-ui");
        return { element: <ReturnsUiPage /> };
      },
    },
    {
      handle: {
        breadcrumb: "Withdrawals",
        permission: [
          SettingsPermissions.SET_GENERAL_SETTINGS,
          SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
        ],
      },
      path: "withdrawals",
      lazy: async () => {
        const { WithdrawalsPage } =
          await import("@redotech/redo-merchant-general-app/setting/withdrawals/withdrawals-page");
        return { Component: WithdrawalsPage };
      },
    },
    {
      handle: {
        breadcrumb: "Shipping",
        permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
      },
      path: "shipping",
      children: [
        {
          path: "",
          lazy: async () => {
            const { ShippingPage } =
              await import("../../../setting/shipping/shipping-page");
            return { Component: ShippingPage };
          },
        },
        {
          handle: {
            breadcrumb: "Location flow",
            permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
          },
          path: "location-flow-editor",
          lazy: async () => {
            const { LocationFlowEditor } =
              await import("../../../setting/flows/location-flow-editor");
            return { Component: LocationFlowEditor };
          },
        },
        {
          handle: {
            breadcrumb: "Label flow",
            permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
          },
          path: "labels-flow-editor",
          lazy: async () => {
            const { LabelsFlowEditor } =
              await import("../../../setting/flows/labels-flow-editor");
            return { Component: LabelsFlowEditor };
          },
        },
      ],
    },
  ],

  [SettingsSectionEnum.WARRANTIES]: [
    {
      handle: {
        breadcrumb: "Registration",
        permission: SettingsPermissions.SET_WARRANTY_SETTINGS,
      },
      path: "registrations",
      lazy: async () => {
        const { WarrantyRegistrationSettings } =
          await import("../../../setting/registration-settings");
        return { element: <WarrantyRegistrationSettings /> };
      },
    },
    {
      handle: {
        breadcrumb: "Registration Rewards",
        permission: SettingsPermissions.SET_WARRANTY_SETTINGS,
      },
      path: "registration-rewards",
      lazy: async () => {
        const { RegistrationRewards } =
          await import("../../../setting/registration-rewards");
        return { element: <RegistrationRewards /> };
      },
    },
  ],

  [SettingsSectionEnum.INTERNATIONAL]: [
    {
      handle: { breadcrumb: "International" },
      path: "international",
      children: [
        { index: true, element: <InternationalIndexRedirect /> },
        {
          handle: {
            breadcrumb: "Customs",
            permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
          },
          path: "customs",
          lazy: async () => {
            const { CustomsSettingsPage } =
              await import("@redotech/redo-merchant-fulfillment-app/setting/customs/customs-page");
            return { Component: CustomsSettingsPage };
          },
        },
        {
          handle: {
            breadcrumb: "Markets",
            permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
          },
          path: "markets",
          children: [
            {
              index: true,
              lazy: async () => {
                const { MarketsPage } =
                  await import("@redotech/redo-merchant-fulfillment-app/setting/markets/markets-page");
                return { Component: MarketsPage };
              },
            },
            {
              handle: { breadcrumb: "New market" },
              path: "new",
              lazy: async () => {
                const { MarketEditorPage } =
                  await import("@redotech/redo-merchant-fulfillment-app/setting/markets/market-editor-page");
                return { Component: MarketEditorPage };
              },
            },
            {
              handle: { breadcrumb: "Edit market" },
              path: ":marketId",
              lazy: async () => {
                const { MarketEditorPage } =
                  await import("@redotech/redo-merchant-fulfillment-app/setting/markets/market-editor-page");
                return { Component: MarketEditorPage };
              },
            },
          ],
        },
        {
          handle: {
            breadcrumb: "Restrictions",
            permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
          },
          path: "restrictions",
          lazy: async () => {
            const { RestrictionsSettingsPage } =
              await import("@redotech/redo-merchant-fulfillment-app/setting/restrictions/restrictions-page");
            return { Component: RestrictionsSettingsPage };
          },
        },
        {
          handle: {
            breadcrumb: "International Returns",
            permission: [
              SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
              SettingsPermissions.SET_SHIPPING_SETTINGS,
            ],
          },
          path: "returns",
          lazy: async () => {
            const { InternationalPage } =
              await import("../../../setting/international/international-page");
            return { Component: InternationalPage };
          },
        },
      ],
    },
  ],

  [SettingsSectionEnum.INVENTORY_MANAGEMENT]: [
    {
      handle: { breadcrumb: "General", permission: GeneralPermissions.OTHER },
      path: "inventory-management/general",
      lazy: async () => {
        const { InventoryManagementGeneralSettingsPage } =
          await import("../../../setting/inventory-management/general-settings");
        return { Component: InventoryManagementGeneralSettingsPage };
      },
    },
    {
      handle: {
        breadcrumb: "Purchase orders",
        permission: GeneralPermissions.OTHER,
      },
      path: "inventory-management/purchase-orders",
      lazy: async () => {
        const { PurchaseOrderSettingsPage } =
          await import("../../../setting/inventory-management/purchase-order-settings");
        return { Component: PurchaseOrderSettingsPage };
      },
    },
    {
      handle: {
        breadcrumb: "QuickBooks Online",
        permission: GeneralPermissions.OTHER,
      },
      path: "inventory-management/quickbooks",
      children: [
        {
          index: true,
          lazy: async () => {
            const { QuickBooksIntegrationSettingsPage } =
              await import("../../../setting/inventory-management/quickbooks-integration-settings");
            return { Component: QuickBooksIntegrationSettingsPage };
          },
        },
        {
          handle: {
            breadcrumb: "QuickBooks account mappings",
            permission: GeneralPermissions.OTHER,
          },
          path: "account-mapping",
          lazy: async () => {
            const { QuickBooksAccountMappingPage } =
              await import("../../../setting/inventory-management/quickbooks-account-mapping");
            return { Component: QuickBooksAccountMappingPage };
          },
        },
      ],
    },
  ],

  [SettingsSectionEnum.ORDER_FULFILLMENT]: [
    {
      handle: {
        breadcrumb: "Order management",
        breadcrumbNavigate: false,
        permission: SettingsPermissions.SET_GENERAL_SETTINGS,
      },
      path: "order-management",
      children: [
        {
          handle: {
            breadcrumb: "Order editing",
            permission: SettingsPermissions.SET_GENERAL_SETTINGS,
          },
          path: "order-editing",
          lazy: async () => {
            const { OrderEditingSettingsPage } =
              await import("../../../setting/order-editing/order-editing-settings");
            return { Component: OrderEditingSettingsPage };
          },
        },
        {
          handle: {
            breadcrumb: "Warehouse",
            permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
          },
          path: "returns-centers",
          children: [
            { path: "", element: <WarehouseSettingsRedirect /> },
            {
              handle: {
                breadcrumb: "Center",
                permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
              },
              path: ":centerId",
              element: <WarehouseSettingsRedirect />,
            },
          ],
        },
      ],
    },
    {
      handle: {
        breadcrumb: "Order management",
        breadcrumbNavigate: false,
        permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
      },
      path: "oms",
      children: [
        {
          handle: {
            breadcrumb: "General",
            permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
          },
          path: "general",
          lazy: async () => {
            const { GeneralOmsSettingsPage } =
              await import("@redotech/redo-merchant-fulfillment-app/setting/general");
            return { Component: GeneralOmsSettingsPage };
          },
        },
        {
          handle: {
            breadcrumb: "Security",
            permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
          },
          path: "security",
          lazy: async () => {
            const { SecuritySettingsPage } =
              await import("@redotech/redo-merchant-fulfillment-app/setting/security");
            return { Component: SecuritySettingsPage };
          },
        },
        {
          handle: {
            breadcrumb: "Scanning & warehouse",
            permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
          },
          path: "scanning-warehouse",
          lazy: async () => {
            const { ScanningWarehouseSettingsPage } =
              await import("@redotech/redo-merchant-fulfillment-app/setting/scanning-warehouse");
            return { Component: ScanningWarehouseSettingsPage };
          },
        },
        {
          handle: {
            breadcrumb: "Printing",
            permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
          },
          path: "printing",
          lazy: async () => {
            const { PrintingSettingsPage } =
              await import("@redotech/redo-merchant-fulfillment-app/setting/printing");
            return { Component: PrintingSettingsPage };
          },
        },
        {
          handle: {
            breadcrumb: "Packing slips",
            permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
          },
          path: "printing/packing-slips",
          children: [
            {
              index: true,
              async lazy() {
                const { PackingSlipsPage } =
                  await import("@redotech/redo-merchant-fulfillment-app/packing-slips/packing-slips-page");
                return { Component: PackingSlipsPage };
              },
            },
            {
              handle: { breadcrumb: "Packing slip builder" },
              path: ":packingSlipId",
              async lazy() {
                const { PackingSlipBuilder } =
                  await import("@redotech/redo-merchant-fulfillment-app/packing-slips/packing-slip-builder");
                return { Component: PackingSlipBuilder };
              },
            },
          ],
        },

        {
          handle: {
            breadcrumb: "Auto-merge shipments",
            permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
          },
          path: "automerge-shipments",
          lazy: async () => {
            const { AutoMergeSettingsPage } =
              await import("@redotech/redo-merchant-fulfillment-app/setting/auto-merge-shipments");
            return { Component: AutoMergeSettingsPage };
          },
        },
        {
          handle: {
            breadcrumb: "Automations",
            permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
          },
          path: "automations",
          children: [
            {
              index: true,
              lazy: async () => {
                const { AutomationsPage: Automations } =
                  await import("@redotech/redo-merchant-fulfillment-app/setting/automations/automations");
                return { Component: Automations };
              },
            },
            {
              handle: {
                breadcrumb: "Automation Builder",
                permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
              },
              path: ":automationId",

              async lazy() {
                const { OmsAutomationBuilder } =
                  await import("../../../setting/oms/automations/automation-builder");
                return { Component: OmsAutomationBuilder };
              },
            },
          ],
        },
        {
          handle: {
            breadcrumb: "Work Order Flows",
            permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
          },
          path: "work-order-flows",
          children: [
            {
              index: true,
              lazy: async () => {
                const { WorkOrderFlowsPage } =
                  await import("@redotech/redo-merchant-fulfillment-app/setting/work-order-flows/work-order-flows");
                return { Component: WorkOrderFlowsPage };
              },
            },
            {
              handle: {
                breadcrumb: "Flow Builder",
                permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
              },
              path: ":flowId",
              async lazy() {
                const { WorkOrderFlowBuilder } =
                  await import("../../../setting/oms/work-order-flows/work-order-flow-builder");
                return { Component: WorkOrderFlowBuilder };
              },
            },
          ],
        },
        {
          path: "balance",
          element: <Navigate replace to="../../billing/outbound" />,
        },
        {
          handle: {
            breadcrumb: "Integrations",
            permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
          },
          path: "integrations",
          element: <IntegrationsHubRedirect to="../../integrations" />,
        },
        {
          handle: {
            breadcrumb: "International",
            permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
          },
          path: "international",
          lazy: async () => {
            const { LoadInternationalSettingsPage } =
              await import("@redotech/redo-merchant-fulfillment-app/setting/international");
            return { Component: LoadInternationalSettingsPage };
          },
        },
        {
          handle: {
            breadcrumb: "Packages",
            permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
          },
          path: "packages",
          lazy: async () => {
            const { ParcelsPage: Parcels } =
              await import("@redotech/redo-merchant-fulfillment-app/setting/parcels");
            return { Component: Parcels };
          },
        },
        {
          handle: {
            breadcrumb: "Presets",
            permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
          },
          path: "presets",
          lazy: async () => {
            const { PresetsPage } =
              await import("@redotech/redo-merchant-fulfillment-app/setting/presets/presets");
            return { Component: PresetsPage };
          },
        },
        {
          handle: {
            breadcrumb: "Reports",
            permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
          },
          path: "reports",
          lazy: async () => {
            const { ReportsPage } =
              await import("@redotech/redo-merchant-fulfillment-app/setting/reports/oms-reports-page");
            return { Component: ReportsPage };
          },
        },
        {
          handle: {
            breadcrumb: "Warehouse builder",
            permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
          },
          path: "warehouse-builder",
          lazy: async () => {
            const { WarehouseBuilderWithProvider } =
              await import("@redotech/redo-merchant-fulfillment-app/wms/warehouse-builder/warehouse-builder");
            return { Component: WarehouseBuilderWithProvider };
          },
        },
        {
          handle: {
            breadcrumb: "Warehouse layouts",
            permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
          },
          path: "warehouse-layouts",
          lazy: async () => {
            const { WarehouseLayoutsPage } =
              await import("@redotech/redo-merchant-fulfillment-app/setting/warehouse-layouts");
            return { Component: WarehouseLayoutsPage };
          },
        },
        {
          handle: {
            breadcrumb: "Custom imports",
            permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
          },
          path: "custom-import-schemas",
          lazy: async () => {
            const { CustomImportSchemas } =
              await import("@redotech/redo-merchant-fulfillment-app/setting/custom-import-schemas");
            return { Component: CustomImportSchemas };
          },
        },
      ],
    },
    { path: "parcels", element: <Navigate replace to="../oms/packages" /> },
    { path: "balance", element: <Navigate replace to="../oms/balance" /> },
    // We moved /locations -> /shipping, so we'll redirect users just in case.
    { path: "locations", element: <Navigate replace to="../shipping" /> },
  ],

  [SettingsSectionEnum.SHIPPING]: [
    {
      handle: {
        breadcrumb: "Carriers",
        permission: SettingsPermissions.SET_SHIPPING_SETTINGS,
      },
      path: "carriers",
      lazy: async () => {
        const { CarriersPage: Carriers } =
          await import("@redotech/redo-merchant-fulfillment-app/setting/carrier-accounts/carriers-page");
        return { Component: Carriers };
      },
    },
    {
      handle: {
        breadcrumb: "Fulfillment promise",
        permission: SettingsPermissions.SET_SHIPPING_SETTINGS,
      },
      path: "fulfillment-cutoff",
      lazy: async () => {
        const { FulfillmentCutoffPage } =
          await import("../../../setting/fulfillment-cutoff/fulfillment-cutoff-page");
        return { Component: FulfillmentCutoffPage };
      },
    },
    {
      handle: {
        breadcrumb: "Freight",
        permission: SettingsPermissions.SET_SHIPPING_SETTINGS,
      },
      path: "freight",
      lazy: async () => {
        const { FreightPage } =
          await import("../../../setting/freight/freight-page");
        return { Component: FreightPage };
      },
    },
    {
      handle: {
        breadcrumb: "Cross border",
        permission: SettingsPermissions.SET_SHIPPING_SETTINGS,
      },
      path: "cross-border",
      lazy: async () => {
        const { LoadCrossBorderSettingsPage } =
          await import("@redotech/redo-merchant-fulfillment-app/setting/cross-border");
        return { Component: LoadCrossBorderSettingsPage };
      },
    },
    {
      handle: {
        breadcrumb: "Discounts",
        permission: SettingsPermissions.SET_SHIPPING_SETTINGS,
      },
      path: "discounts",
      lazy: async () => {
        const { LoadShippingDiscountsPage } =
          await import("@redotech/redo-merchant-fulfillment-app/setting/discounts");
        return { Component: LoadShippingDiscountsPage };
      },
    },
  ],

  [SettingsSectionEnum.RECOVER]: [],

  [SettingsSectionEnum.SUPPORT]: [
    {
      handle: {
        breadcrumb: "General",
        permission: SettingsPermissions.SET_SUPPORT_SETTINGS,
      },
      path: "general",
      children: [
        {
          index: true,
          lazy: async () => {
            const { SupportGeneral } =
              await import("@redotech/redo-merchant-support-app/setting/general");
            return { element: <SupportGeneral /> };
          },
        },
        {
          handle: { breadcrumb: "Contact Form" },
          path: "contact-form",
          lazy: async () => {
            const { ContactFormSettingsNew } =
              await import("@redotech/redo-merchant-support-app/setting/general/contact-form-settings-new");
            return { Component: ContactFormSettingsNew };
          },
        },
      ],
    },
    {
      handle: {
        breadcrumb: "Channels",
        permission: SettingsPermissions.SET_SUPPORT_SETTINGS,
      },
      path: "channels",
      children: [
        {
          path: "",
          lazy: async () => {
            const { ChannelsPage } = await import("../../../setting/channels");
            return { Component: ChannelsPage };
          },
        },
        {
          handle: { breadcrumb: "Support Email" },
          path: "supportEmail",
          lazy: async () => {
            const { SupportEmailInfo } =
              await import("@redotech/redo-merchant-support-app/setting/email-info");
            return { Component: SupportEmailInfo };
          },
        },
        {
          handle: { breadcrumb: "Gmail Dev Tools" },
          path: "gmail-dev/:integrationId",
          lazy: async () => {
            const { GmailDevTools } =
              await import("@redotech/redo-merchant-support-app/setting/gmail-dev-tools");
            return { Component: GmailDevTools };
          },
        },
        {
          handle: { breadcrumb: "Outlook Dev Tools" },
          path: "outlook-dev/:integrationId",
          lazy: async () => {
            const { OutlookDevTools } =
              await import("@redotech/redo-merchant-support-app/setting/outlook-dev-tools");
            return { Component: OutlookDevTools };
          },
        },
        {
          handle: { breadcrumb: "Facebook Dev Tools" },
          path: "facebook-dev/:integrationId",
          lazy: async () => {
            const { FacebookDevTools } =
              // redont-allow-dynamic-import: Preserve route-level code splitting, matching the existing Gmail and Outlook dev-tool routes.
              await import("@redotech/redo-merchant-support-app/setting/facebook-dev-tools");
            return { Component: FacebookDevTools };
          },
        },
        {
          handle: { breadcrumb: "Instagram Dev Tools" },
          path: "instagram-dev/:integrationId",
          lazy: async () => {
            const { InstagramDevTools } =
              // redont-allow-dynamic-import: Preserve route-level code splitting, matching the existing Gmail and Outlook dev-tool routes.
              await import("@redotech/redo-merchant-support-app/setting/instagram-dev-tools");
            return { Component: InstagramDevTools };
          },
        },
        {
          handle: { breadcrumb: "Tiktok" },
          path: "tiktok-callback",
          async lazy() {
            const { Callback } =
              await import("@redotech/redo-merchant-support-app/tiktok-callback");
            return { element: <Callback /> };
          },
        },
        {
          handle: { breadcrumb: "TikTok Shop" },
          path: "tiktok-settings",
          async lazy() {
            const { TiktokSettings } =
              await import("../../../setting/channel/tiktok-settings");
            return { element: <TiktokSettings /> };
          },
        },
        {
          handle: { breadcrumb: "Meta" },
          path: "meta-connect",
          async lazy() {
            const { Connect } =
              await import("@redotech/redo-merchant-support-app/meta/connect");
            return { element: <Connect /> };
          },
        },
        {
          handle: { breadcrumb: "Meta" },
          path: "meta-settings",
          async lazy() {
            const { MetaSettings } =
              await import("../../../setting/channel/meta-settings");
            return { element: <MetaSettings /> };
          },
        },
        {
          handle: { breadcrumb: "Voice" },
          path: "voice-settings",
          async lazy() {
            const { VoiceAndSmsSettings } =
              await import("../../../setting/channel/voice-and-sms/voice-and-sms-settings");
            return { element: <VoiceAndSmsSettings /> };
          },
        },
        {
          handle: { breadcrumb: "Phone" },
          path: "phone-setup",
          async lazy() {
            const { PhoneSetup } =
              await import("@redotech/redo-merchant-support-app/phone/setup");
            return { element: <PhoneSetup /> };
          },
        },
        {
          handle: { breadcrumb: "Email" },
          path: "email-connect",
          async lazy() {
            const { EmailSetup } = await import("../../../setting/email-setup");
            return { element: <EmailSetup /> };
          },
        },
        {
          path: "meta-callback",
          async lazy() {
            const { Callback } =
              await import("@redotech/redo-merchant-support-app/meta/meta-callback");
            return { element: <Callback /> };
          },
        },
        {
          handle: { breadcrumb: "Attentive" },
          path: "attentive-connect",
          async lazy() {
            const { Connect } =
              await import("@redotech/redo-merchant-support-app/attentive/connect");
            return { element: <Connect /> };
          },
        },
        {
          handle: { breadcrumb: "Postscript" },
          path: "postscript-connect",
          async lazy() {
            const { Connect } =
              await import("@redotech/redo-merchant-support-app/postscript/connect");
            return { element: <Connect /> };
          },
        },
        {
          handle: { breadcrumb: "Gorgias" },
          path: "gorgias-connect",
          async lazy() {
            const { Connect } =
              await import("@redotech/redo-merchant-support-app/gorgias/connect");
            return { element: <Connect /> };
          },
        },
        {
          handle: { breadcrumb: "Richpanel" },
          path: "richpanel-connect",
          async lazy() {
            const { Connect } =
              await import("@redotech/redo-merchant-support-app/richpanel/connect");
            return { element: <Connect /> };
          },
        },
        {
          path: "attentive-callback",
          async lazy() {
            const { Callback } =
              await import("@redotech/redo-merchant-support-app/attentive-callback");
            return { element: <Callback /> };
          },
        },
        {
          path: "gmail-callback",
          async lazy() {
            const { Callback } =
              await import("@redotech/redo-merchant-support-app/gmail-callback");
            return { element: <Callback /> };
          },
        },
        {
          path: "outlook-callback",
          async lazy() {
            const { Callback } =
              await import("@redotech/redo-merchant-support-app/outlook-callback");
            return { element: <Callback /> };
          },
        },
      ],
    },
    {
      handle: {
        breadcrumb: "CSAT",
        permission: SettingsPermissions.SET_SUPPORT_SETTINGS,
      },
      path: "customer-satisfaction",
      element: <CsatSettingsRedirect />,
    },
    {
      handle: {
        breadcrumb: "Tags",
        permission: SettingsPermissions.SET_SUPPORT_SETTINGS,
      },
      path: "tags",
      lazy: async () => {
        const { Tags } =
          await import("../../../setting/conversation-tags/tags");
        return { Component: Tags };
      },
    },
    {
      handle: {
        breadcrumb: "FAQ",
        permission: SettingsPermissions.SET_SUPPORT_SETTINGS,
      },
      path: "faq",
      element: <FaqRedirect />,
    },
    {
      handle: {
        breadcrumb: "Macros",
        permission: SettingsPermissions.SET_SUPPORT_SETTINGS,
      },
      path: "macros",
      lazy: async () => {
        const { SupportV2MacrosPage } =
          await import("@redotech/redo-merchant-support-app/v2/setting/macros/macros");
        return { Component: SupportV2MacrosPage };
      },
    },
    {
      handle: {
        breadcrumb: "Topics",
        permission: SettingsPermissions.SET_SUPPORT_SETTINGS,
      },
      path: "topics",
      lazy: async () => {
        const { SupportV2TopicsPage } =
          await import("@redotech/redo-merchant-support-app/v2/setting/topics/topics");
        return { Component: SupportV2TopicsPage };
      },
    },
    {
      handle: {
        breadcrumb: "Rules",
        permission: SettingsPermissions.SET_SUPPORT_SETTINGS,
      },
      path: "rules",
      children: [
        {
          path: "",
          lazy: async () => {
            const { RulesListRouter } =
              await import("@redotech/redo-merchant-support-app/setting/rules/rules-router");
            return { element: <RulesListRouter /> };
          },
        },
        {
          handle: { breadcrumb: "New rule" },
          path: "new",
          lazy: async () => {
            const { CreateRuleRouter } =
              await import("@redotech/redo-merchant-support-app/setting/rules/rules-router");
            return { element: <CreateRuleRouter /> };
          },
        },
        {
          handle: { breadcrumb: "Edit rule" },
          path: "edit/:ruleId",
          lazy: async () => {
            const { CreateRuleRouter } =
              await import("@redotech/redo-merchant-support-app/setting/rules/rules-router");
            return { element: <CreateRuleRouter /> };
          },
        },
        {
          handle: { breadcrumb: "Edit rule" },
          path: "edit-v2/:ruleId",
          lazy: async () => {
            const { CreateRuleRouter } =
              await import("@redotech/redo-merchant-support-app/setting/rules/rules-router");
            return { element: <CreateRuleRouter /> };
          },
        },
        {
          handle: { breadcrumb: "Edit rule" },
          path: "edit-v2/",
          lazy: async () => {
            const { CreateRuleRouter } =
              await import("@redotech/redo-merchant-support-app/setting/rules/rules-router");
            return { element: <CreateRuleRouter /> };
          },
        },
        {
          handle: { breadcrumb: "Edit rule" },
          path: "edit-v3/:ruleId",
          lazy: async () => {
            const { CreateRuleRouter } =
              await import("@redotech/redo-merchant-support-app/setting/rules/rules-router");
            return { element: <CreateRuleRouter /> };
          },
        },
        {
          handle: { breadcrumb: "Edit rule" },
          path: "edit-v3/",
          lazy: async () => {
            const { CreateRuleRouter } =
              await import("@redotech/redo-merchant-support-app/setting/rules/rules-router");
            return { element: <CreateRuleRouter /> };
          },
        },
        {
          handle: { breadcrumb: "Edit default rule" },
          path: "edit-default/:defaultRuleId",
          lazy: async () => {
            const { EditDefaultRulePage } =
              await import("@redotech/redo-merchant-support-app/setting/rules/edit-default-rule-page");
            return { element: <EditDefaultRulePage /> };
          },
        },
      ],
    },
  ],
};

const routeChildren = Object.values(settingsRoutesBySection).reduce<
  PermissionedRouteObject[]
>((acc, section) => {
  if (Array.isArray(section)) {
    return [...acc, ...section];
  } else {
    return [...acc, section];
  }
}, [] as PermissionedRouteObject[]);

// Add redirects for old routes
routeChildren.push(
  // Redirect old try-first routes to conversion/try-first
  {
    path: "try-first",
    element: <Navigate replace to="../conversion/try-first" />,
  },
  {
    path: "try-first/*",
    element: <Navigate replace to="../conversion/try-first" />,
  },

  // Redirect old checkout-optimization routes to conversion/delivery-estimate
  {
    path: "checkout-optimization",
    element: <Navigate replace to="../conversion/delivery-estimate" />,
  },
  {
    path: "checkout-optimization/*",
    element: <Navigate replace to="../conversion/delivery-estimate" />,
  },
);

export const settingRoutes: PermissionedRouteObject[] = [
  {
    handle: { breadcrumb: "Settings", breadcrumbNavigate: false },
    lazy: async () => {
      const { Page } = await import("../../page");
      return {
        element: (
          <SettingNavigationProvider>
            <Page
              hideHeader={(url) =>
                fullscreen(url) ||
                inForge(url) ||
                inPhoneSetup(url) ||
                inNewFlowEditor(url) ||
                inLocationEditor(url) ||
                inLabelsEditor(url) ||
                inReturnProcessingFlowBuilder(url) ||
                inPortalBuilder(url) ||
                inCoverageBuilder(url)
              }
              hideNavbar={(url) =>
                fullscreen(url) ||
                inForge(url) ||
                inPhoneSetup(url) ||
                inNewFlowEditor(url) ||
                inLocationEditor(url) ||
                inLabelsEditor(url) ||
                inReturnProcessingFlowBuilder(url) ||
                inPortalBuilder(url) ||
                inCoverageBuilder(url)
              }
              hidePadding={hidePadding}
            />
          </SettingNavigationProvider>
        ),
      };
    },
    children: routeChildren,
  },
];
